import { useEffect } from "react";
import { Outlet } from "react-router-dom";
import { useProfile } from "@/hooks/useApi";
import {
  applyTheme,
  cacheThemePreference,
  isThemePreference,
  readCachedThemePreference,
  subscribeToSystemTheme,
  type ThemePreference,
} from "@/lib/theme";

function useAppliedTheme(preference: ThemePreference, cache: boolean) {
  useEffect(() => {
    if (cache) cacheThemePreference(preference);
    applyTheme(preference);
    if (preference !== "system") return;
    return subscribeToSystemTheme(() => applyTheme("system"));
  }, [preference, cache]);
}

/** Public pages always track the operating-system color scheme. */
export function SystemThemeLayout() {
  useAppliedTheme("system", false);
  return <Outlet />;
}

/** Authenticated pages use the preference persisted on the user's profile. */
export function UserThemeLayout() {
  const { data: profile } = useProfile();
  const storedPreference = profile?.themePreference;
  const preference = isThemePreference(storedPreference)
    ? storedPreference
    : (readCachedThemePreference() ?? "system");

  useAppliedTheme(preference, true);
  return <Outlet />;
}
